import React, { useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { StructureContainer, StructureHeader, Title } from '../style'
import InputFiles from '../../../../../components/ui/InputFiles'
import {
  EditInfosAbout,
  Image,
  ImageContainer,
  ImageInfo,
  ImageOrderBadge,
  ImagesContainer,
  RemoveImage,
  TitleOrSubtitle,
  TitleOrSubtitleInput,
  DragHandle,
} from './style'
import { GripVertical, ImageIcon, Pen, X } from 'lucide-react'

interface ImageData {
  id?: number
  file?: File
  fileUrl?: string
  customName: string
  isEditing: boolean
  subtitle?: string
}

interface ImagesInTheCertificateProps {
  images: ImageData[]
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>
}

const getSortableId = (imageData: ImageData, index: number) => {
  if (imageData.id != null) return `id-${imageData.id}`
  if (imageData.fileUrl) return `url-${imageData.fileUrl}`
  if (imageData.file) {
    return `file-${imageData.file.name}-${imageData.file.size}-${imageData.file.lastModified}`
  }
  return `idx-${index}-${imageData.customName}`
}

const getImageSrc = (imageData: ImageData) => {
  if (imageData.file) return URL.createObjectURL(imageData.file)
  if (imageData.fileUrl) return imageData.fileUrl
  return ''
}

const getImageSize = (imageData: ImageData) => {
  if (imageData.file) return (imageData.file.size / 1024 / 1024).toFixed(1)
  return 'N/A'
}

type SortableImageProps = {
  imageData: ImageData
  index: number
  sortableId: string
  onRemove: (index: number) => void
  onStartEditing: (index: number) => void
  onSaveEdit: (index: number, newName: string) => void
  onCancelEdit: (index: number) => void
  onChangeCustomName: (index: number, value: string) => void
  onChangeSubtitle: (index: number, value: string) => void
}

const SortableImageCard: React.FC<SortableImageProps> = ({
  imageData,
  index,
  sortableId,
  onRemove,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onChangeCustomName,
  onChangeSubtitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : undefined,
  }

  return (
    <ImageContainer ref={setNodeRef} style={style} $isDragging={isDragging}>
      <ImageOrderBadge>{index + 1}.</ImageOrderBadge>
      <DragHandle type="button" aria-label="Reordenar anexo" {...attributes} {...listeners}>
        <GripVertical size={18} />
      </DragHandle>
      <RemoveImage type="button" onClick={() => onRemove(index)}>
        <X size={18} strokeWidth={3} />
      </RemoveImage>
      <Image draggable={false} src={getImageSrc(imageData)} alt={imageData.customName} />
      <ImageInfo>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ImageIcon size={18} />
          <span>{getImageSize(imageData)} MB</span>
        </div>

        <EditInfosAbout style={{ marginBottom: '.5rem' }}>
          <span>Título da imagem</span>
          {imageData.isEditing ? (
            <TitleOrSubtitleInput
              autoFocus
              value={imageData.customName}
              onChange={(e) => onChangeCustomName(index, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSaveEdit(index, imageData.customName)
                } else if (e.key === 'Escape') {
                  onCancelEdit(index)
                }
              }}
              onBlur={() => onSaveEdit(index, imageData.customName)}
            />
          ) : (
            <TitleOrSubtitle onClick={() => onStartEditing(index)}>
              {imageData.customName}
              <Pen size={14} />
            </TitleOrSubtitle>
          )}
        </EditInfosAbout>

        <EditInfosAbout>
          <span>Subtítulo da imagem</span>
          <TitleOrSubtitleInput
            value={imageData.subtitle}
            onChange={(e) => onChangeSubtitle(index, e.target.value)}
          />
        </EditInfosAbout>
      </ImageInfo>
    </ImageContainer>
  )
}

const ImagesInTheCertificate: React.FC<ImagesInTheCertificateProps> = ({ images, setImages }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const sortableItems = useMemo(
    () => images.map((imageData, index) => ({
      imageData,
      index,
      sortableId: getSortableId(imageData, index),
    })),
    [images],
  )

  const handleAddImage = (file: File) => {
    setImages([
      ...images,
      {
        file,
        customName: file.name,
        isEditing: false,
      },
    ])
  }

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index))
  }

  const handleStartEditing = (index: number) => {
    setImages(images.map((img, i) => (i === index ? { ...img, isEditing: true } : img)))
  }

  const handleSaveEdit = (index: number, newName: string) => {
    setImages(
      images.map((img, i) =>
        i === index ? { ...img, customName: newName, isEditing: false } : img,
      ),
    )
  }

  const handleCancelEdit = (index: number) => {
    setImages(
      images.map((img, i) =>
        i === index
          ? {
              ...img,
              customName: img.file ? img.file.name : img.customName,
              isEditing: false,
            }
          : img,
      ),
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sortableItems.findIndex((item) => item.sortableId === active.id)
    const newIndex = sortableItems.findIndex((item) => item.sortableId === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    setImages((prev) => arrayMove(prev, oldIndex, newIndex))
  }

  return (
    <StructureContainer>
      <StructureHeader style={{ marginBottom: '30px' }}>
        <Title>Imagens no Certificado</Title>
      </StructureHeader>
      <InputFiles handleAddImage={handleAddImage} />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableItems.map((item) => item.sortableId)}
          strategy={rectSortingStrategy}
        >
          <ImagesContainer>
            {sortableItems.map(({ imageData, index, sortableId }) => (
              <SortableImageCard
                key={sortableId}
                imageData={imageData}
                index={index}
                sortableId={sortableId}
                onRemove={handleRemoveImage}
                onStartEditing={handleStartEditing}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onChangeCustomName={(i, value) => {
                  setImages(images.map((img, idx) =>
                    idx === i ? { ...img, customName: value } : img,
                  ))
                }}
                onChangeSubtitle={(i, value) => {
                  setImages(images.map((img, idx) =>
                    idx === i ? { ...img, subtitle: value } : img,
                  ))
                }}
              />
            ))}
          </ImagesContainer>
        </SortableContext>
      </DndContext>
    </StructureContainer>
  )
}

export default ImagesInTheCertificate
